/**
 * Tide-height port selection + favourites, persisted across restarts. The chosen
 * reference port and the user's starred favourites both survive a reload, so the
 * Tides screen opens where they left it and their home ports stay at the top of
 * the picker. The heights engine still owns the port DATA (src/tides/heights.ts);
 * this only remembers WHICH port is selected and which ids are favourited.
 *
 * The selected id is mirrored into the environment slice's heightPortId by the
 * Tides sheet so the rest of the app (the tide data box, the feature sheet's
 * height-of-tide) keeps reading one value. This store is the persisted home of
 * that choice; the env slice is the live read the older code already uses.
 */
import { create } from 'zustand';
import { DEFAULT_HEIGHT_PORT_ID } from '@/tides/heights';

const KEY = 'acp.tideports.v1';

interface Persisted {
  /**
   * Manually chosen FES station id, or null for "nearest to the boat". A
   * skipper planning tomorrow's leg wants the DESTINATION's tide, not the tide
   * where the boat happens to sit tonight (fb-msmaupvz-0).
   */
  fesStationId: string | null;
  selectedPortId: string;
  favourites: string[];
}

const DEFAULTS: Persisted = {
  selectedPortId: DEFAULT_HEIGHT_PORT_ID,
  favourites: [],
  fesStationId: null,
};

function load(): Persisted {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return { ...DEFAULTS };
    const p = JSON.parse(raw) as Partial<Persisted>;
    return {
      selectedPortId: typeof p.selectedPortId === 'string' ? p.selectedPortId : DEFAULTS.selectedPortId,
      favourites: Array.isArray(p.favourites) ? p.favourites.filter((x): x is string => typeof x === 'string') : [],
      fesStationId: typeof p.fesStationId === 'string' ? p.fesStationId : null,
    };
  } catch {
    return { ...DEFAULTS };
  }
}
function persist(p: Persisted): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

interface TidePortsState extends Persisted {
  setSelectedPortId: (id: string) => void;
  toggleFavourite: (id: string) => void;
  isFavourite: (id: string) => boolean;
  /** null = follow the boat (nearest station). */
  setFesStationId: (id: string | null) => void;
}

export const useTidePortsStore = create<TidePortsState>((set, get) => ({
  ...load(),
  setSelectedPortId: (selectedPortId) => {
    set({ selectedPortId });
    persist({ selectedPortId, favourites: get().favourites, fesStationId: get().fesStationId });
  },
  setFesStationId: (fesStationId) => {
    set({ fesStationId });
    persist({ selectedPortId: get().selectedPortId, favourites: get().favourites, fesStationId });
  },
  toggleFavourite: (id) => {
    const cur = get().favourites;
    const favourites = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    set({ favourites });
    persist({ selectedPortId: get().selectedPortId, favourites, fesStationId: get().fesStationId });
  },
  isFavourite: (id) => get().favourites.includes(id),
}));
